import { useEffect, useRef, type RefObject, type ReactNode } from 'react';

/**
 * Focus trap (Sprint BA、docs/16 §10-2 a11y 横断)。
 *
 * Modal / Drawer / Popover 等の overlay 系で、Tab / Shift+Tab フォーカスを
 * 内部の focusable element 内に閉じ込める。WAI-ARIA Authoring Practices の
 * dialog pattern 準拠:
 *   - mount 時に最初の focusable element に focus を移す
 *   - Tab 末尾 → 先頭、Shift+Tab 先頭 → 末尾 で wrap-around
 *   - active=false なら何もしない (後方互換のため optional)
 *
 * Modal 既存挙動 (Esc / overlay click) は壊さず、独立に動く設計。
 *
 * Hook 形式:
 *   const ref = useRef<HTMLDivElement>(null);
 *   useFocusTrap(ref, { active: open });
 *
 * Component 形式 (内部で hook を使うラッパ、用途は限定的):
 *   <FocusTrap active={open}>...</FocusTrap>
 */

/** Tab 可能な element の selector (button / input / [tabindex>=0] / ...)。 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

function getFocusable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((el) => {
    // tabIndex -1 / disabled は selector で弾いているが、display:none / visibility:hidden は別途。
    if (el.hasAttribute('disabled')) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;
    return true;
  });
}

export interface UseFocusTrapOptions {
  /** trap を有効にするか。false ならイベントを登録しない (後方互換)。default true。 */
  active?: boolean;
  /** mount 時に最初の focusable element に focus を移すか。default true。 */
  autoFocus?: boolean;
}

/**
 * 与えた ref の DOM subtree に focus trap を仕込む hook。
 * Modal が open=false のとき component 自体が unmount されるため、active 切替は
 * 主に Modal の `open` 状態と連動して使う。
 */
export function useFocusTrap<T extends HTMLElement>(
  ref: RefObject<T>,
  { active = true, autoFocus = true }: UseFocusTrapOptions = {},
): void {
  useEffect(() => {
    if (!active) return;
    const root = ref.current;
    if (!root) return;

    // 元の active element を覚えておき、unmount 時に focus を戻す。
    const previouslyFocused = document.activeElement as HTMLElement | null;

    if (autoFocus) {
      const focusables = getFocusable(root);
      // 先頭 element に focus。1 つも無い場合 root 自体に tabindex=-1 + focus で
      // fallback (Modal panel の click-stop pattern と整合)。
      const head = focusables[0];
      if (head) {
        head.focus();
      } else {
        if (!root.hasAttribute('tabindex')) root.setAttribute('tabindex', '-1');
        root.focus();
      }
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      if (!root) return;
      const focusables = getFocusable(root);
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;
      const activeEl = document.activeElement as HTMLElement | null;

      // フォーカスが trap 外に出ていた場合は先頭に戻す。
      if (!activeEl || !root.contains(activeEl)) {
        e.preventDefault();
        first.focus();
        return;
      }

      if (e.shiftKey) {
        if (activeEl === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (activeEl === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // 閉じたとき元の element に focus を戻す (DOM 接続中で focusable な場合のみ)。
      if (previouslyFocused && document.contains(previouslyFocused)) {
        try {
          previouslyFocused.focus();
        } catch {
          // ignore — element が detached or non-focusable な場合
        }
      }
    };
  }, [ref, active, autoFocus]);
}

export interface FocusTrapProps extends UseFocusTrapOptions {
  children: ReactNode;
  /** root に付与する className. */
  className?: string;
}

/**
 * `useFocusTrap` の宣言的ラッパ。`<div>` を 1 段挟むため、Modal のように
 * 既存 DOM を維持したい場合は hook 形式を直接使うほうが好ましい。
 */
export function FocusTrap({
  active = true,
  autoFocus = true,
  className,
  children,
}: FocusTrapProps) {
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(ref, { active, autoFocus });
  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}
